<template>
  <div
    class="bg-base-100 text-base-content h-full flex flex-col overflow-hidden"
  >
    <!-- Back button header -->
    <ThreadsNav show-back :show-center="false" />

    <div v-if="loading" class="h-full pt-20 text-center">
      <div class="loading loading-ring loading-lg"></div>
    </div>

    <ThreadList
      class="flex-1"
      ref="threadList"
      :on-pull-load="() => reload(true)"
      :on-scroll-load="() => loadNextPage()"
      v-if="category"
    >
      <div class="flex-1">
        <!-- Title section -->
        <div class="p-4">
          <h1 class="text-2xl font-bold">{{ category?.item.name }}</h1>
        </div>

        <!-- Character progress cards -->
        <div class="px-4 pb-4 flex overflow-x-auto">
          <div
            v-for="(mate, index) in category?.matezz"
            :key="index"
            :class="[
              'character-card flex flex-col items-center space-y-2 min-w-12',
              activeMate?.id === mate.id ? '' : 'grayscale',
            ]"
            @click="
              (activeMate = activeMate?.id === mate.id ? undefined : mate),
                reload()
            "
          >
            <div>
              <div
                :class="[
                  'w-8 rounded border',
                  activeMate?.id === mate.id
                    ? 'border-primary'
                    : 'border-transparent',
                ]"
              >
                <UserAvatar :mate="mate" />
              </div>
            </div>
            <div class="text-center">
              <p class="text-xs font-medium line-clamp-1">
                {{ useName(mate.name) }}
              </p>
              <p class="text-xs text-gray-500">完成度</p>
              <p class="text-xs font-medium">
                {{ mate.answerProgressPercent }}%
              </p>
            </div>
          </div>
        </div>

        <!-- Tab navigation -->
        <VanTabs v-model:active="activeTab" @change="clickTab">
          <VanTab title="未回答" name="unanswered" />
          <VanTab title="已回答" name="answered" />
          <VanTab title="全部问题" name="all" />
        </VanTabs>

        <!-- Question list -->
        <div class="p-4 space-y-4">
          <div
            v-for="(question, index) in questions"
            :key="index"
            class="card bg-base-200 shadow-md hover:shadow-lg transition-shadow cursor-pointer"
            @click="goToDetail(question)"
          >
            <div class="card-body p-4">
              <h3 class="text-base font-bold">{{ question.text }}</h3>
              <p class="text-xs text-base-content/70">
                {{ question.explanation }}
              </p>
              <template v-if="question.answerzz.length">
                <div v-for="item in question.answerzz" :key="item.id">
                  <div class="flex flex-col gap-2">
                    <div class="rounded-lg flex items-center gap-1">
                      <div class="w-10">
                        <UserAvatar :mate="item.mate" />
                      </div>
                      <p class="text-xs text-base-content/70 flex-1">
                        {{ item.mate!.name }}：
                      </p>
                    </div>
                    <p
                      class="text-xs text-base-content/70 prose prose-sm"
                      v-html="item.text"
                    />
                  </div>
                </div>
              </template>
            </div>
          </div>
        </div>
      </div>
    </ThreadList>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, nextTick } from "vue";
import { useRouter, useRoute } from "vue-router";
import {
  ApiGetRoleplayQuestionCategory,
  ApiGetRoleplayQuestionMatePage,
} from "@/apis/roleplay";
import {
  Mate,
  RoleplayQuestion,
  RoleplayQuestionCategory,
} from "@/types/entity";
import { ThreadListInstance } from "@/components/mobile/ThreadList.vue";
import { useMate } from "@/stores/mate";
import { useI18n } from "vue-i18n";
import { useName } from "@/use/common";

const router = useRouter();
const route = useRoute();
const activeTab = ref("unanswered");
const activeMate = ref<Mate>();
const { t } = useI18n();

const threadList = ref<ThreadListInstance>();
let mateFirstLoad = true;

const reload = async (reloadMateList = false) => {
  questions.value = [];
  threadList.value?.resetScrollLoadingState();
  if (reloadMateList) {
    await reloadCategory();
  }
};

const clickTab = (tab: "all" | "answered" | "unanswered") => {
  activeTab.value = tab;
  reload();
};

const questions = ref<RoleplayQuestion[]>([]);

const category = ref<{
  matezz: Array<Mate & { answerProgressPercent: number }>;
  item: RoleplayQuestionCategory;
}>();

const loadNextPage = async () => {
  const { length } = questions.value;
  const page = length / 5 + 1;
  if (length !== 0 && length % 5 !== 0) return false;
  const list = await ApiGetRoleplayQuestionMatePage({
    pagination: {
      page,
      perPage: 5,
    },
    filter: {
      mateId: activeMate.value?.id,
      categoryId: category.value!.item.id,
      type: activeTab.value as "all" | "answered" | "unanswered",
    },
    sort: {
      field: "dtUpdate",
      order: "desc",
    },
  });
  if (
    questions.value.length === 0 &&
    list.page.data.length === 0 &&
    mateFirstLoad &&
    activeTab.value === "unanswered"
  ) {
    mateFirstLoad = false;
    nextTick(() => clickTab("all"));
  }
  questions.value = [...questions.value, ...list.page.data];
  return list.page.data.length === 5;
};

const goToDetail = (question: RoleplayQuestion) => {
  router.push({
    name: "roleplayDetail",
    params: { uuid: question.uuid },
    query: {
      mateId: activeMate.value?.id,
    },
  });
};

const reloadCategory = async () => {
  const res = await ApiGetRoleplayQuestionCategory(route.params.uuid as string);

  if (res.data.matezz.length === 0) {
    const hasMate = await useMate().checkHasMate(router.back);
    if (!hasMate) return;
  }

  category.value = res.data;
  if (!activeMate.value && category.value?.matezz.length) {
    activeMate.value = category.value.matezz[0];
  }
};
const loading = ref(false);

onMounted(async () => {
  loading.value = true;
  await reloadCategory();
  if (route.query.mateUuid) {
    activeMate.value = category.value?.matezz.find(
      (mate) => mate.uuid === route.query.mateUuid
    );
  }
  loading.value = false;
});
</script>
